import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import type {
  Language,
  Translations,
  I18nContextType,
  LanguageConfig,
} from "@/types/i18n";
import { userApi } from "@/api/aiApi";
import { useGlobalStore } from "@/store/store";
import AuthService from "@/services/auth";
import EventBus from "@/utils/eventBus";
import { toast } from "sonner";
import { envStorage } from "@/utils/envCache";

// 导入语言文件
import zhCN from "@/locales/ZH_CN.json";
import enUS from "@/locales/EN_US.json";
import zhHK from "@/locales/ZH_HK.json";

// 语言配置
export const LANGUAGE_CONFIGS: LanguageConfig[] = [
  {
    code: "ZH_CN",
    name: "Chinese (Simplified)",
    nativeName: "简体中文",
  },
  {
    code: "ZH_HK",
    name: "Chinese (Traditional)",
    nativeName: "繁體中文",
  },
  {
    code: "EN_US",
    name: "English",
    nativeName: "English",
  },
];

// 翻译资源
const translations: Record<Language, Translations> = {
  ZH_CN: zhCN as Translations,
  ZH_HK: zhHK as Translations,
  EN_US: enUS as Translations,
};

// 默认语言
const DEFAULT_LANGUAGE: Language = "ZH_HK";

// 本地存储键名
const LANGUAGE_STORAGE_KEY = "bee-chatbot-language";

// 创建Context
const I18nContext = createContext<I18nContextType | undefined>(undefined);

// 获取浏览器语言
function getBrowserLanguage(): Language {
  const browserLang = navigator.language || navigator.languages?.[0];

  if (browserLang?.startsWith("zh")) {
    // 检测繁体中文地区
    if (
      browserLang.includes("HK") ||
      browserLang.includes("TW") ||
      browserLang.includes("MO")
    ) {
      return "ZH_HK";
    }
    return "ZH_CN";
  }
  if (browserLang?.startsWith("en")) {
    return "EN_US";
  }

  return DEFAULT_LANGUAGE;
}

// 获取初始语言：优先从 userInfo.lang，然后本地存储，最后浏览器语言
function getInitialLanguage(): Language {
  try {
    // 首先尝试从 userInfo 获取语言设置
    const userInfo = AuthService.getUserInfo();
    if (userInfo?.lang && translations[userInfo.lang as Language]) {
      return userInfo.lang as Language;
    }

    // 其次从本地存储获取
    const stored = envStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
    if (stored && translations[stored]) {
      return stored;
    }
  } catch (error) {
    console.warn("Failed to get language from localStorage:", error);
  }

  // 最后使用默认语言（繁体中文）
  return DEFAULT_LANGUAGE;
}

// 翻译函数
function getTranslation(translations: Translations, key: string, params?: Record<string, any>): string {
  const keys = key.split(".");
  let result: any = translations;

  for (const k of keys) {
    if (result && typeof result === "object" && k in result) {
      result = result[k];
    } else {
      console.warn(`Translation key not found: ${key}`);
      return key; // 返回原始key作为fallback
    }
  }

  let translation = typeof result === "string" ? result : key;
  
  // 处理插值
  if (params && typeof translation === "string") {
    Object.keys(params).forEach(paramKey => {
      // 支持双大括号格式 {{paramKey}}
      const doubleBraceRegex = new RegExp(`{{${paramKey}}}`, 'g');
      translation = translation.replace(doubleBraceRegex, String(params[paramKey]));
      
      // 支持双引号格式 "{paramKey}"
      const quotedRegex = new RegExp(`"{${paramKey}}"`, 'g');
      translation = translation.replace(quotedRegex, `"${String(params[paramKey])}"`);
    });
  }

  return translation;
}

// Provider组件
interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const { user, setUser } = useGlobalStore();

  // 设置语言并保存到本地存储
  const setLanguage = async (newLanguage: Language) => {
    if (translations[newLanguage]) {
      setLanguageState(newLanguage);
      try {
        envStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
        // 调用API更新语言设置
        const response = await userApi.updateProfile({ lang: newLanguage });

        // 更新本地用户信息中的语言设置
        if (user) {
          const updatedUserInfo = {
            ...user,
            lang: newLanguage,
          };

          // 更新全局状态
          setUser(updatedUserInfo);

          // 更新本地存储
          AuthService.setUserInfo(updatedUserInfo);
        }

        // 显示成功提示
        toast.success(
          getTranslation(translations[newLanguage], "status.updateSuccess")
        );
      } catch (error) {
        console.warn("Failed to save language:", error);
        // 显示错误提示
        toast.error(
          getTranslation(translations[newLanguage], "status.updateFailed")
        );
      }
    } else {
      console.warn(`Unsupported language: ${newLanguage}`);
    }
  };

  // 更新用户昵称
  const updateNickName = async (nickName: string) => {
    try {
      const response = await userApi.updateProfile({ nickName });
      console.log("昵称更新成功");

      // 如果 API 返回了完整的用户信息，更新全局状态和本地存储
      if (response.data) {
        const updatedUserInfo = {
          ...user,
          ...response.data,
          nickname: response.data.nickname || nickName,
        };

        // 更新全局状态
        setUser(updatedUserInfo);

        // 更新本地存储
        AuthService.setUserInfo(updatedUserInfo);
      } else {
        // 如果 API 没有返回完整信息，只更新昵称字段
        const updatedUserInfo = {
          ...user,
          nickname: nickName,
        };

        // 更新全局状态
        setUser(updatedUserInfo);

        // 更新本地存储
        AuthService.setUserInfo(updatedUserInfo);
      }
    } catch (error) {
      console.error("昵称更新失败:", error);
      throw error;
    }
  };

  // 翻译函数
  const t = (key: string, params?: Record<string, any>): string => {
    return getTranslation(translations[language], key, params);
  };

  // 将翻译函数添加到全局 window 对象，供类组件使用
  useEffect(() => {
    (window as any).__t = t;
  }, [language]);

  // 设置HTML lang属性
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // 监听用户信息更新事件
  useEffect(() => {
    const handleUserInfoUpdate = (userInfo: any) => {
      setUser(userInfo);
      // 如果用户信息中包含语言设置，同步更新语言
      if (userInfo.lang && userInfo.lang !== language) {
        setLanguageState(userInfo.lang);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, userInfo.lang);
      }
    };

    EventBus.on('userInfoUpdated', handleUserInfoUpdate);
    
    return () => {
      EventBus.off('userInfoUpdated', handleUserInfoUpdate);
    };
  }, [language, setUser]);

  const value: I18nContextType = {
    language,
    translations: translations[language],
    setLanguage,
    updateNickName,
    t,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Hook
export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

// 便捷的翻译Hook
export function useTranslation() {
  const { t, language, setLanguage, updateNickName } = useI18n();
  return { t, language, setLanguage, updateNickName };
}

// DEFAULT_LANGUAGE 导出
export { DEFAULT_LANGUAGE };
